import React, { useMemo } from 'react';
import { createAvatar } from '@dicebear/core';
// DiceBear style packages export a namespace ({ meta, create, ... }), not a
// default — `createAvatar` expects the namespace itself.
import * as botttsNeutral from '@dicebear/bottts-neutral';
import './AnimalAvatar.css';

// Module-level cache: same seed → same SVG, no point recomputing on every
// render. DiceBear is pure so memoization is safe; we cap the cache at a
// modest size with FIFO eviction so a long-running chat doesn't grow it
// without bound.
const CACHE_LIMIT = 500;
const cache = new Map();

function buildSvg(seed) {
  if (cache.has(seed)) {
    // Move-to-front so popular seeds survive eviction.
    const v = cache.get(seed);
    cache.delete(seed);
    cache.set(seed, v);
    return v;
  }
  const svg = createAvatar(botttsNeutral, { seed }).toString();
  cache.set(seed, svg);
  if (cache.size > CACHE_LIMIT) {
    // FIFO drop: oldest insertion first.
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
  return svg;
}

/**
 * AnimalAvatar — visitor avatar primitive.
 *
 * Backed by DiceBear's `bottts-neutral` style (cute monochrome bots).
 * Deterministic from `seed`. The wrapper is a rounded-square (~22% radius)
 * to match Glacier's design language of soft-cornered tiles rather than the
 * round avatars common to chat apps.
 *
 * Props:
 *   seed       (string)              required — drives the deterministic art
 *   size       (number, default 40)  the square edge in px
 *   ring       (bool | string)       when truthy, draws a 1.5px outline on the
 *                                    wrapper (string → custom color; true →
 *                                    currentColor)
 *   className  (string)              forwarded to the wrapper
 *
 * The component name is preserved for backwards compatibility with the
 * 40-odd existing imports across nav, chat, onboarding, and user surfaces.
 * It's no longer specifically "animal" art — the name has just outlived
 * its meaning.
 */
const AnimalAvatar = ({ seed = 'glob', size = 40, ring = false, className }) => {
  const svgMarkup = useMemo(() => buildSvg(String(seed)), [seed]);

  const wrapperClass = ['glacier-avatar', className].filter(Boolean).join(' ');
  const style = {
    width: size,
    height: size,
  };
  if (ring) {
    style.boxShadow = `0 0 0 1.5px ${
      typeof ring === 'string' ? ring : 'currentColor'
    }`;
  }

  return (
    <span
      className={wrapperClass}
      role="img"
      aria-label={`Avatar for ${seed}`}
      style={style}
      data-seed={seed}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
    />
  );
};

export default AnimalAvatar;
// Exported for tests / hot-paths that want to pre-warm the cache.
export const __clearAvatarCache = () => cache.clear();
